import { Component, ElementRef, inject, input, ViewChild, OnDestroy, signal, effect, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { ChartOptions } from '../shared/models/nox-chart';
import { ApexAxisChartSeries, NgApexchartsModule, } from 'ng-apexcharts';
import { BaseComponent } from '../base/components/base.component';
import { LargeScalePlantGenerationResponse } from '../shared/models/large-scale-plant-generation';
import { ProductService } from '../shared/service/product.service';
import { CspNonceService } from '../shared/service/csp-nonce.service';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
    selector: 'app-large-scale-plant-generation',
    imports: [TranslateModule, NgApexchartsModule],
    templateUrl: './large-scale-plant-generation.component.html',
    styleUrl: './large-scale-plant-generation.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LargeScalePlantGenerationComponent extends BaseComponent implements OnDestroy {
  plantID = input<string>();
  
  private readonly productService = inject(ProductService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cspNonceService = inject(CspNonceService);
  
  series = signal<ApexAxisChartSeries>([]);
  dates = signal<string[]>([]);
  energyData = signal<number[]>([]);
  irradianceData = signal<number[]>([]);
  showChart = signal(false);
  chartOptions: Partial<ChartOptions> = {
    legend: { show: false }
  };

  @ViewChild('chart') chart!: any;
  @ViewChild('chartWrapper') chartWrapper!: ElementRef;
  
  private readonly renderTimeout!: ReturnType<typeof setTimeout>;
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly cspNonceServiceInject: CspNonceService) {
    super();
    effect(() => {
      const id = this.plantID?.();
      if (id) {
        this.getPlantGeneration();
      }
    });
  }

  getPlantGeneration(): void {
    const id = this.plantID?.();
    if (!id) return;
    
    this.productService
      .getPlantGenerationData(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: LargeScalePlantGenerationResponse) => {
          const generationData = response.data.generationData
            .slice()
            .sort(
              (a, b) =>
                new Date(a.date).getTime() - new Date(b.date).getTime()
            );
          const newDates: string[] = [];
          const newEnergyData: number[] = [];
          const newIrradianceData: number[] = [];
          
          generationData.forEach(d => {
            newDates.push(
              new Date(d.date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short'
              })
            );
            newEnergyData.push(Number.parseFloat(d.energyGeneration));
            newIrradianceData.push(d.irradiation);
          });
          
          this.dates.set(newDates);
          this.energyData.set(newEnergyData);
          this.irradianceData.set(newIrradianceData);
          this.plotPlantGeneration();
          this.showChart.set(true);
        },
        error: (err: Error) => {
          this.logger.error(err.message);
        }
      });
  }

  plotPlantGeneration(): void {
    const maxEnergy = Math.max(...this.energyData());
    const energyMin = 0;
    const energyMax = Math.ceil(maxEnergy / 1000) * 1000;
    const energyTickAmount = energyMax / 1000;
    const maxIrr = Math.max(...this.irradianceData());
    const irrMin = 0;
    const irrMax = Math.ceil(maxIrr);
    const irrTickAmount = irrMax;
    const nonce = this.cspNonceServiceInject.getNonce() ?? undefined;
    this.chartOptions = {
      series: [
        {
          name: 'Energy Generation',
          type: 'column',
          data: this.energyData()
        },
        {
          name: 'Irradiation',
          type: 'line',
          data: this.irradianceData(),
          color: '#FF9500'
        }
      ],

      chart: {
        height: 300,
        type: 'bar',
        width: '100%',
        nonce: nonce,
        toolbar: { show: true },
        foreColor: '#E5E7EB',
        animations: { enabled: false },
        redrawOnWindowResize: true,
        redrawOnParentResize: true

      },
      dataLabels: {
        enabled: false
      },
      grid: {
        borderColor: '#2E3440'
      },

      stroke: {
        width: [0, 2],
        curve: 'smooth',
        colors: ['#FF9500']
      },
      markers: {
        size: [0, 3],
        colors: ['#FFFFFF'],
        strokeWidth: 2,
        strokeColors: 'FFFFFF',
        hover: {
          size: 9
        },
        offsetY: -0.5
      },

      fill: {
        type: ['gradient', 'solid'],
        gradient: {
          type: 'horizontal',
          shadeIntensity: 1,
          gradientToColors: ['#769154'],
          opacityFrom: 0.7,
          opacityTo: 0.9,
          colorStops: [
            { offset: 0, color: '#0D3F7F', opacity: 0.7 },
            { offset: 70, color: '#769154', opacity: 0.7 },
            { offset: 100, color: '#769154', opacity: 0.9 }
          ]
        }
      },
      plotOptions: {
        bar: {
          columnWidth: '30%',
          borderRadius: 4
        }
      },
      xaxis: {
        categories: this.dates(),
        crosshairs: {
          show: true,
          width: 40,
        }
      },
      yaxis: [
        {
          min: energyMin,
          max: energyMax,
          tickAmount: energyTickAmount,
          forceNiceScale: false,
          decimalsInFloat: 0,
          title: {
            text: 'Energy Generation (kWh)'
          },
          labels: {
            formatter: (val) => Math.round(val).toString()
          }
        },
        {
          min: irrMin,
          max: irrMax,
          tickAmount: irrTickAmount,
          forceNiceScale: false,
          decimalsInFloat: 0,
          opposite: true,
          title: {
            text: 'Irradiation  (kWh/m²)'
          }
        }
      ],
      tooltip: {
        shared: true,
        intersect: false,
        followCursor: true,
        marker: {
          fillColors: ['#769154', '#FF9500']
        },
        y: {
          formatter: (value: number | undefined, opts) => {
            const { seriesIndex, w } = opts;

            const isSeriesHidden = w.globals.collapsedSeriesIndices.includes(seriesIndex);

            if (isSeriesHidden || value === undefined) {
              return '';
            }
            if (seriesIndex === 0) {
              return `${value.toFixed(2)} kWh`;
            }
            if (seriesIndex === 1) {
              return `${value.toFixed(2)} kWh/m²`;
            }
            return '';
          }
        }
      }
      ,
      legend: {
        position: 'bottom',
        labels: {
          useSeriesColors: false,
          colors: '#FFFFFF'
        },
        markers: {
          fillColors: ['#769154', '#FF9500']
        }
      }


    };
  }
  ngOnDestroy(): void {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
